import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, MapPin, Phone, Mail, Building2 } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import Button from '../common/Button';
import { contactService } from '../../services/contactService';

const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    location: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await contactService.createContact(formData);
      setSuccess(true);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        location: '',
        subject: '',
        message: ''
      });

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBusinessHours = () => {
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 100 + now.getMinutes();

    const businessHours = {
      0: { name: 'Sunday', open: 1000, close: 1600 },
      1: { name: 'Monday', open: 900, close: 1900 },
      2: { name: 'Tuesday', open: 900, close: 1900 },
      3: { name: 'Wednesday', open: 900, close: 1900 },
      4: { name: 'Thursday', open: 900, close: 1900 },
      5: { name: 'Friday', open: 900, close: 1900 },
      6: { name: 'Saturday', open: 900, close: 1700 }
    };

    const today = businessHours[currentDay];
    const isOpen = currentTime >= today.open && currentTime <= today.close;

    return {
      isOpen,
      currentDay,
      timeString: now.toLocaleString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
      })
    };
  };

  const [businessStatus, setBusinessStatus] = useState(updateBusinessHours());

  useEffect(() => {
    const interval = setInterval(() => {
      setBusinessStatus(updateBusinessHours());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />

      <section className="bg-gradient-to-r from-brand to-brand-hover text-white pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="container mx-auto px-4 lg:px-6 text-center">
          <p className="text-brand-light text-sm font-medium uppercase tracking-widest mb-3">
            Get In Touch
          </p>
          <h1 className="text-3xl md:text-5xl font-semibold mb-4">Contact Us</h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Have questions? We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8 lg:gap-10">
            {/* Contact Form */}
            <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 md:p-8">
              <h2 className="text-xl font-semibold text-brand mb-6">Send us a Message</h2>

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-card mb-6 text-sm">
                  Message sent successfully! We&apos;ll get back to you soon.
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-card mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Enter your first name"
                      className="input-clean"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Enter your last name"
                      className="input-clean"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Email Address <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      className="input-clean"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className="input-clean"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Enter your location (optional)"
                      className="input-clean"
                    />
                  </div>
                  <div>
                    <label className="form-label">Subject <span className="text-danger">*</span></label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="input-clean"
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="quote">Request Quote</option>
                      <option value="support">Customer Support</option>
                      <option value="partnership">Partnership</option>
                      <option value="complaint">Complaint</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="form-label">Message <span className="text-danger">*</span></label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Enter your message"
                    className="input-clean resize-none"
                    required
                  />
                </div>
                <Button type="submit" variant="primary" size="lg" loading={loading}>
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Sidebar Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-card shadow-card border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-dark mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-brand" />
                  Business Hours
                </h3>

                <div className={`mb-4 p-3 rounded-card text-sm ${businessStatus.isOpen ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-dark">Current Status:</span>
                    <span className={`font-semibold ${businessStatus.isOpen ? 'text-success' : 'text-danger'}`}>
                      {businessStatus.isOpen ? 'OPEN' : 'CLOSED'}
                    </span>
                  </div>
                  <div className="text-secondary mt-1">{businessStatus.timeString}</div>
                </div>

                <div className="space-y-3 text-sm">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day, idx) => (
                    <div key={day} className={`flex justify-between ${businessStatus.currentDay === idx + 1 ? 'text-brand font-semibold' : ''}`}>
                      <span className="text-secondary">{day}:</span>
                      <span className="text-dark font-medium">9:00 AM - 7:00 PM</span>
                    </div>
                  ))}
                  <div className={`flex justify-between ${businessStatus.currentDay === 6 ? 'text-brand font-semibold' : ''}`}>
                    <span className="text-secondary">Saturday:</span>
                    <span className="text-dark font-medium">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className={`flex justify-between ${businessStatus.currentDay === 0 ? 'text-brand font-semibold' : ''}`}>
                    <span className="text-secondary">Sunday:</span>
                    <span className="text-dark font-medium">10:00 AM - 4:00 PM</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-card p-6">
                <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-danger" />
                  Emergency Support
                </h3>
                <p className="text-red-700 text-sm mb-2">For urgent shipment issues or emergencies:</p>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-danger" />
                  <span className="text-red-800 font-medium">+91 94805 01861</span>
                </div>
                <p className="text-sm text-red-600 mt-2">Available 24/7 for emergency support</p>
              </div>
            </div>
          </div>

          {/* Locations */}
          <div className="mt-16 md:mt-20">
            <div className="text-center mb-10">
              <p className="text-brand font-semibold text-sm uppercase tracking-widest mb-3">Our Locations</p>
              <h2 className="text-2xl md:text-3xl font-semibold text-dark">
                Visit us at our office in Mumbai
              </h2>
            </div>

            <div className="flex justify-center">
              <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 md:p-8 max-w-xl w-full">
                <div className="flex items-center mb-5 gap-3">
                  <div className="w-10 h-10 bg-brand-light rounded-card flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-brand" />
                  </div>
                  <h3 className="font-semibold text-dark">Office Address</h3>
                  <span className="ml-auto bg-brand-light text-brand text-xs font-semibold px-3 py-1 rounded-btn">
                    Head Office
                  </span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                    <div className="text-secondary">
                      <p>Plot No 13, Shivaji Co-Operative Housing Society LTD,</p>
                      <p>Crystal Compound, Sion Trombay Road, Mumbai.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-brand shrink-0" />
                    <span className="font-medium text-dark">+91 94805 01861</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-brand shrink-0" />
                    <span className="text-secondary">admin@metrologistics.co.in</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
